import { styled } from "goober";
import { useEffect, useId, useRef, useState } from "react";

const loadPele = () => import("pele");

const diagramPadding = 24;
const diagramBorder = 0.5;

const Diagram = styled("div")`
  overflow-x: auto;
  padding: ${diagramPadding}px;
  background: var(--alpha4);
  border: ${diagramBorder}px solid var(--border-alpha);
  border-radius: 12px;
  --pele-bg: var(--surface-secondary);
  --pele-fg: var(--text-primary);
  --pele-muted: var(--text-secondary);
  --pele-line: var(--text-secondary);
  --pele-surface: var(--alpha4);
  --pele-surface-alt: var(--alpha4);
  --pele-border: var(--text-secondary);
  --pele-accent: var(--purple-600);
  --pele-critical: var(--red-600);
  --pele-radius: 8px;
  --pele-series-1: var(--sky-600);
  --pele-series-2: var(--orange-600);
  --pele-series-3: var(--green-600);
  --pele-series-4: var(--red-600);
  --pele-series-5: var(--teal-600);
  --pele-series-6: var(--yellow-600);
  --pele-series-7: var(--purple-600);
  --pele-series-8: var(--pink-600);

  svg {
    display: block;
    margin: auto;
    max-width: 100%;
    height: auto;
  }
`;

export function Mermaid({ source }: { source: string }) {
  const idPrefix = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;
    let observer: ResizeObserver | undefined;

    (async () => {
      try {
        const { render } = await loadPele();
        if (cancelled) return;
        let width = container.clientWidth;
        const draw = () => {
          if (cancelled) return;
          try {
            const style = getComputedStyle(container);
            const { svg } = render(source, {
              idPrefix,
              maxWidth: Math.max(
                1,
                width - 2 * (diagramPadding + diagramBorder)
              ),
              fontFamily: style.fontFamily,
              fontFamilyMono: style.getPropertyValue("--font-mono").trim(),
              links: false,
              images: false,
            });
            setSvg(svg);
          } catch {
            setSvg(null);
          }
        };
        draw();
        observer = new ResizeObserver(() => {
          const nextWidth = container.clientWidth;
          if (nextWidth === width) return;
          width = nextWidth;
          draw();
        });
        observer.observe(container);
        if (document.fonts?.status === "loading")
          void document.fonts.ready.then(draw);
      } catch {
        if (!cancelled) setSvg(null);
      }
    })();

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [source, idPrefix]);

  return (
    <div ref={containerRef}>
      {svg !== null ? (
        <Diagram dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <pre>
          <code>{source}</code>
        </pre>
      )}
    </div>
  );
}
